import { RepaymentType, RefinanceEvent, Scenario, StrategyType } from './types.js';
import { scheduleHorizon } from './schedule.js';
import { calendarPaymentDate, isCalendarDate } from './bank-calendar.js';

export type ValidationCode =
  | 'principal_positive'
  | 'term_positive'
  | 'term_too_long'
  | 'rate_invalid'
  | 'repayment_type'
  | 'strategy'
  | 'start_date'
  | 'calculation_mode'
  | 'bank_calendar'
  | 'rate_period_range'
  | 'rate_period_rate'
  | 'rate_periods_overlap'
  | 'rate_period_beyond_term'
  | 'refinance_month'
  | 'refinance_rate'
  | 'refinance_term'
  | 'refinance_repayment_type'
  | 'refinance_duplicate'
  | 'refinance_beyond_term'
  | 'overpayment_month'
  | 'overpayment_beyond_term'
  | 'overpayment_amount'
  | 'overpayment_amount_zero'
  | 'overpayment_strategy'
  | 'overpayment_exclusive'
  | 'recurring_step'
  | 'recurring_end'
  | 'adaptive_step'
  | 'adaptive_target'
  | 'adaptive_min'
  | 'adaptive_min_exceeds_target'
  | 'adaptive_end'
  | 'schedule'
  | 'shape';

/** `code` is stable and meant for UI localisation; `message` is the English reference text. */
export type ValidationIssue = { field: string; code: ValidationCode; message: string };

/**
 * Upper bound for the loan term and any refinance term (50 years). The form offers 5–35
 * years; the cap protects the schedule builder from a hand-made link or storage entry
 * (a term of millions of months would freeze the tab).
 */
export const MAX_TERM_MONTHS = 600;

const REPAYMENT_TYPES: readonly RepaymentType[] = ['fixed', 'declining'];
const STRATEGIES: readonly StrategyType[] = ['reduce_term', 'reduce_payment'];
const YEAR_MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

function isNonNegativeInteger(value: unknown): boolean {
  return Number.isInteger(value) && (value as number) >= 0;
}

function isPositiveInteger(value: unknown): boolean {
  return Number.isInteger(value) && (value as number) > 0;
}

function isNonNegativeRate(value: unknown): boolean {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

/**
 * Month at which the contract running at `month` ends: the term set by the latest
 * refinance before `month` (each refinance replaces the schedule, so an earlier,
 * shorter one may already have closed the loan), else the original term. A
 * refinance at or beyond this month would never be applied.
 */
function runningUntil(s: Scenario, month: number, refinances: readonly RefinanceEvent[]): number {
  const latest = refinances.reduce<RefinanceEvent | null>(
    (best, r) => (r.month < month && isPositiveInteger(r.newTermMonths) && (!best || r.month > best.month) ? r : best),
    null,
  );
  return latest ? latest.month + latest.newTermMonths : s.termMonths;
}

/**
 * Business-rule validation of a structurally well-formed scenario. Returns an
 * empty list when the scenario can be scheduled. Every amount must be an
 * integer number of cents, every month an integer offset from the start date.
 */
export function validateScenario(s: Scenario): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  if (!isPositiveInteger(s.principal)) {
    issues.push({ field: 'principal', code: 'principal_positive', message: 'Principal must be a positive integer amount of cents' });
  }
  if (!isPositiveInteger(s.termMonths)) {
    issues.push({ field: 'termMonths', code: 'term_positive', message: 'Term must be a positive integer number of months' });
  } else if (s.termMonths > MAX_TERM_MONTHS) {
    issues.push({ field: 'termMonths', code: 'term_too_long', message: `Term must not exceed ${MAX_TERM_MONTHS} months` });
  }
  if (!isNonNegativeRate(s.rateApr)) {
    issues.push({ field: 'rateApr', code: 'rate_invalid', message: 'Rate must be a finite non-negative percentage' });
  }
  if (!REPAYMENT_TYPES.includes(s.repaymentType)) {
    issues.push({ field: 'repaymentType', code: 'repayment_type', message: 'Repayment type must be fixed or declining' });
  }
  if (!STRATEGIES.includes(s.overpaymentStrategy)) {
    issues.push({ field: 'overpaymentStrategy', code: 'strategy', message: 'Strategy must be reduce_term or reduce_payment' });
  }
  if (typeof s.startDate !== 'string' || !YEAR_MONTH.test(s.startDate)) {
    issues.push({ field: 'startDate', code: 'start_date', message: 'Start date must be YYYY-MM' });
  }
  if (s.calculationMode !== undefined && !['monthly', 'bank_calendar'].includes(s.calculationMode)) {
    issues.push({ field: 'calculationMode', code: 'calculation_mode', message: 'Unknown calculation mode' });
  }
  if (s.calculationMode === 'bank_calendar') {
    const bank = s.bankCalendar;
    const dateValid = bank && typeof bank.disbursementDate === 'string' && isCalendarDate(bank.disbursementDate);
    const dayValid = bank && isPositiveInteger(bank.paymentDay) && bank.paymentDay <= 31;
    if (!bank || !dateValid || !dayValid
      || (bank.recalculationMonths !== undefined && (!Array.isArray(bank.recalculationMonths)
        || bank.recalculationMonths.some(month => !isPositiveInteger(month) || month >= s.termMonths)))
      || (dateValid && dayValid && YEAR_MONTH.test(s.startDate)
        && calendarPaymentDate(s.startDate, 0, bank.paymentDay) <= bank.disbursementDate)) {
      issues.push({ field: 'bankCalendar', code: 'bank_calendar', message: 'Calendar mode requires valid dates and recalculation months within the term' });
    }
  }

  // Bounds are checked only for well-formed months so that one mistake yields one issue.
  const termValid = isPositiveInteger(s.termMonths) && s.termMonths <= MAX_TERM_MONTHS;
  const horizon = scheduleHorizon(s);
  const horizonValid = termValid && Number.isFinite(horizon) && horizon > 0;
  const validRefinances = s.refinances.filter(r => isPositiveInteger(r.month));

  s.ratePeriods.forEach((p, idx) => {
    if (!isNonNegativeInteger(p.startMonth) || !isNonNegativeInteger(p.endMonth) || p.endMonth < p.startMonth) {
      issues.push({ field: `ratePeriods[${idx}]`, code: 'rate_period_range', message: 'Invalid start/end month' });
    }
    if (!isNonNegativeRate(p.rateApr)) {
      issues.push({ field: `ratePeriods[${idx}]`, code: 'rate_period_rate', message: 'Rate must be a finite non-negative percentage' });
    }
    if (horizonValid && isNonNegativeInteger(p.startMonth) && p.startMonth >= horizon) {
      issues.push({ field: `ratePeriods[${idx}]`, code: 'rate_period_beyond_term', message: 'Rate period must start within the loan term' });
    }
  });

  const sortedPeriods = [...s.ratePeriods].sort((a, b) => a.startMonth - b.startMonth);
  for (let i = 1; i < sortedPeriods.length; i++) {
    const prev = sortedPeriods[i - 1];
    const current = sortedPeriods[i];
    if (current.startMonth <= prev.endMonth) {
      issues.push({ field: 'ratePeriods', code: 'rate_periods_overlap', message: 'Discounted rate periods must not overlap' });
      break;
    }
  }

  const refinanceMonths = new Set<number>();
  s.refinances.forEach((r, idx) => {
    if (!isPositiveInteger(r.month)) {
      issues.push({ field: `refinances[${idx}]`, code: 'refinance_month', message: 'Refinance month must be a positive integer' });
    } else if (termValid && r.month >= runningUntil(s, r.month, validRefinances)) {
      issues.push({ field: `refinances[${idx}]`, code: 'refinance_beyond_term', message: 'Refinance month must fall within the running loan term' });
    }
    if (!isNonNegativeRate(r.newRateApr)) {
      issues.push({ field: `refinances[${idx}]`, code: 'refinance_rate', message: 'Refinance rate must be a finite non-negative percentage' });
    }
    if (!isPositiveInteger(r.newTermMonths)) {
      issues.push({ field: `refinances[${idx}]`, code: 'refinance_term', message: 'Refinance term must be a positive integer number of months' });
    } else if (r.newTermMonths > MAX_TERM_MONTHS) {
      issues.push({ field: `refinances[${idx}]`, code: 'term_too_long', message: `Refinance term must not exceed ${MAX_TERM_MONTHS} months` });
    }
    if (r.repaymentType !== undefined && !REPAYMENT_TYPES.includes(r.repaymentType)) {
      issues.push({ field: `refinances[${idx}]`, code: 'refinance_repayment_type', message: 'Repayment type must be fixed or declining' });
    }
    if (refinanceMonths.has(r.month)) {
      issues.push({ field: 'refinances', code: 'refinance_duplicate', message: 'Refinance months must be unique' });
    }
    refinanceMonths.add(r.month);
  });

  s.overpayments.forEach((o, idx) => {
    const field = `overpayments[${idx}]`;
    if (!isNonNegativeInteger(o.month)) {
      issues.push({ field, code: 'overpayment_month', message: 'Overpayment month must be a non-negative integer' });
    } else if (horizonValid && o.month >= horizon) {
      issues.push({ field, code: 'overpayment_beyond_term', message: 'Overpayment month must fall within the loan term' });
    }
    if (!isNonNegativeInteger(o.amount)) {
      issues.push({ field, code: 'overpayment_amount', message: 'Overpayment amount must be a non-negative integer amount of cents' });
    } else if (o.amount === 0 && !o.adaptive) {
      // Adaptive overpayments compute their amount every month, so 0 is allowed there.
      issues.push({ field, code: 'overpayment_amount_zero', message: 'Overpayment amount must be positive' });
    }
    if (o.strategy !== undefined && !STRATEGIES.includes(o.strategy)) {
      issues.push({ field, code: 'overpayment_strategy', message: 'Strategy must be reduce_term or reduce_payment' });
    }
    if (o.recurring && o.adaptive) {
      issues.push({ field, code: 'overpayment_exclusive', message: 'Overpayment cannot be both recurring and adaptive' });
    }
    if (o.recurring) {
      if (o.recurring.step !== 1) {
        issues.push({ field, code: 'recurring_step', message: 'Recurring step must be 1' });
      }
      if (o.recurring.endMonth !== undefined && (!isNonNegativeInteger(o.recurring.endMonth) || o.recurring.endMonth < o.month)) {
        issues.push({ field, code: 'recurring_end', message: 'Recurring end month must be an integer not before the start month' });
      } else if (o.recurring.endMonth !== undefined && horizonValid && o.recurring.endMonth >= horizon) {
        issues.push({ field, code: 'overpayment_beyond_term', message: 'Recurring end month must fall within the loan term' });
      }
    }
    if (o.adaptive) {
      if (o.adaptive.step !== 1) {
        issues.push({ field, code: 'adaptive_step', message: 'Adaptive step must be 1' });
      }
      if (!isPositiveInteger(o.adaptive.targetAmount)) {
        issues.push({ field, code: 'adaptive_target', message: 'Adaptive target amount must be a positive integer amount of cents' });
      }
      if (!isNonNegativeInteger(o.adaptive.minAmount)) {
        issues.push({ field, code: 'adaptive_min', message: 'Adaptive minimum amount must be a non-negative integer amount of cents' });
      } else if (isPositiveInteger(o.adaptive.targetAmount) && o.adaptive.minAmount > o.adaptive.targetAmount) {
        issues.push({ field, code: 'adaptive_min_exceeds_target', message: 'Adaptive minimum amount cannot exceed the target amount' });
      }
      if (o.adaptive.endMonth !== undefined && (!isNonNegativeInteger(o.adaptive.endMonth) || o.adaptive.endMonth < o.month)) {
        issues.push({ field, code: 'adaptive_end', message: 'Adaptive end month must be an integer not before the start month' });
      } else if (o.adaptive.endMonth !== undefined && horizonValid && o.adaptive.endMonth >= horizon) {
        issues.push({ field, code: 'overpayment_beyond_term', message: 'Adaptive end month must fall within the loan term' });
      }
    }
  });

  return issues;
}

